import ReportTranslationService from '../Services/reportTranslation.service';
import { applyWorkbookLtr, applyWorkbookRtl } from './ExcelReport';

/**
 * Report translation: reports are generated in Hebrew; this module translates the generated
 * HTML / Excel workbook into another language and mirrors the layout for LTR languages.
 * The report language is chosen per report and is independent of the UI language.
 */

export const SOURCE_REPORT_LANGUAGE = 'he';
export const REPORT_LANGUAGES = ['he', 'en', 'ar', 'ru'];

export const isRtlLanguage = (language) => language === 'he' || language === 'ar';
export const getReportDirection = (language) => (isRtlLanguage(language) ? 'rtl' : 'ltr');

const HEBREW_LETTER = /[\u05D0-\u05EA]/;
const NUMBER_PATTERN = /\d+(?:[.,/:-]\d+)*/g;
const PLACEHOLDER_PATTERN = /\{(\d+)\}/g;
const CSS_HEBREW_STRING = /(["'])((?:(?!\1)[^\\]|\\.)*?[\u05D0-\u05EA](?:(?!\1)[^\\]|\\.)*?)\1/g;
const MAX_TEXTS_PER_REQUEST = 1000;

const sessionCache = new Map();
const cacheKey = (language, text) => `${language}\u0000${text}`;

/**
 * Numbers (amounts, dates, codes) are replaced with {n} placeholders so that strings differing
 * only by numbers share a single cached translation.
 */
const toTemplate = (text) => {
    const values = [];
    const template = text.replace(NUMBER_PATTERN, (match) => {
        values.push(match);
        return `{${values.length - 1}}`;
    });
    return { template, values };
};

const fromTemplate = (template, values) =>
    template.replace(PLACEHOLDER_PATTERN, (match, index) => values[Number(index)] ?? match);

/** Splits surrounding whitespace off so only the meaningful core of a string is translated. */
const splitWhitespace = (text) => {
    const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(text);
    return { leading: match[1], core: match[2], trailing: match[3] };
};

const needsTranslation = (text) => !!text && HEBREW_LETTER.test(text);

/**
 * Translates templates into the target language, using the session cache first.
 * Returns a map template -> translation (falls back to the template itself).
 */
const translateTemplates = async (templates, language, token) => {
    const unique = [...new Set(templates)];
    const missing = unique.filter((t) => !sessionCache.has(cacheKey(language, t)));

    for (let i = 0; i < missing.length; i += MAX_TEXTS_PER_REQUEST) {
        const chunk = missing.slice(i, i + MAX_TEXTS_PER_REQUEST);
        const translations = await ReportTranslationService.translateReportTerms(chunk, language, token);
        chunk.forEach((t) => sessionCache.set(cacheKey(language, t), translations[t] || t));
    }

    const map = {};
    unique.forEach((t) => {
        map[t] = sessionCache.get(cacheKey(language, t)) ?? t;
    });
    return map;
};

/**
 * Collects translatable strings, translates them in one batch, and returns a function that
 * maps an original string to its translation (whitespace and numbers preserved).
 */
const createTranslator = async (texts, language, token) => {
    const templates = [];
    texts.forEach((text) => {
        const { core } = splitWhitespace(text);
        if (needsTranslation(core)) templates.push(toTemplate(core).template);
    });

    const map = templates.length ? await translateTemplates(templates, language, token) : {};

    return (text) => {
        const { leading, core, trailing } = splitWhitespace(text);
        if (!needsTranslation(core)) return text;
        const { template, values } = toTemplate(core);
        const translated = map[template];
        return translated ? `${leading}${fromTemplate(translated, values)}${trailing}` : text;
    };
};

const swapLeftRight = (text) =>
    text.replace(/\b(left|right)\b/gi, (word) => {
        const lower = word.toLowerCase();
        const swapped = lower === 'left' ? 'right' : 'left';
        return word[0] === word[0].toUpperCase()
            ? swapped[0].toUpperCase() + swapped.slice(1)
            : swapped;
    });

/** Mirrors CSS declarations (rtl -> ltr, left <-> right) without touching selectors or url() data. */
const mirrorDeclarations = (declarations) => {
    const urls = [];
    const masked = declarations.replace(/url\([^)]*\)/gi, (url) => {
        urls.push(url);
        return `url(\u0000${urls.length - 1}\u0000)`;
    });
    return swapLeftRight(masked.replace(/direction\s*:\s*rtl/gi, 'direction: ltr'))
        .replace(/url\(\u0000(\d+)\u0000\)/g, (match, index) => urls[Number(index)]);
};

const mirrorStylesheet = (css) => css.replace(/\{[^{}]*\}/g, mirrorDeclarations);

const mirrorDocumentToLtr = (doc) => {
    doc.querySelectorAll('style').forEach((style) => {
        style.textContent = mirrorStylesheet(style.textContent);
    });
    doc.querySelectorAll('[style]').forEach((el) => {
        el.setAttribute('style', mirrorDeclarations(el.getAttribute('style')));
    });
    doc.querySelectorAll('[dir="rtl"]').forEach((el) => el.setAttribute('dir', 'ltr'));
    doc.querySelectorAll('[align]').forEach((el) => {
        el.setAttribute('align', swapLeftRight(el.getAttribute('align')));
    });
};

const collectTextNodes = (doc) => {
    const nodes = [];
    const walker = doc.createTreeWalker(doc.documentElement, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
            const parentTag = node.parentNode?.nodeName;
            if (parentTag === 'SCRIPT' || parentTag === 'STYLE') return NodeFilter.FILTER_REJECT;
            return needsTranslation(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        },
    });
    while (walker.nextNode()) nodes.push(walker.currentNode);
    return nodes;
};

const collectCssStrings = (doc) => {
    const strings = [];
    doc.querySelectorAll('style').forEach((style) => {
        for (const match of style.textContent.matchAll(CSS_HEBREW_STRING)) strings.push(match[2]);
    });
    return strings;
};

/**
 * Translates a full report HTML document into `language` and sets its direction.
 * Returns the original HTML untouched for the source language (Hebrew).
 */
export const translateReportHtml = async (html, language, token) => {
    if (!html || !language || language === SOURCE_REPORT_LANGUAGE) return html;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const textNodes = collectTextNodes(doc);
    const cssStrings = collectCssStrings(doc);

    const translate = await createTranslator(
        [...textNodes.map((n) => n.nodeValue), ...cssStrings],
        language,
        token
    );

    textNodes.forEach((node) => {
        node.nodeValue = translate(node.nodeValue);
    });
    doc.querySelectorAll('style').forEach((style) => {
        style.textContent = style.textContent.replace(
            CSS_HEBREW_STRING,
            (match, quote, value) => `${quote}${translate(value)}${quote}`
        );
    });

    const direction = getReportDirection(language);
    doc.documentElement.setAttribute('lang', language);
    doc.documentElement.setAttribute('dir', direction);
    doc.body?.setAttribute('dir', direction);
    if (direction === 'ltr') mirrorDocumentToLtr(doc);

    return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
};

const mirrorCellStyleToLtr = (style) => {
    const alignment = style?.alignment;
    if (!alignment) return style;
    const next = { ...alignment };
    if (next.horizontal === 'left') next.horizontal = 'right';
    else if (next.horizontal === 'right') next.horizontal = 'left';
    if (next.readingOrder === 2) next.readingOrder = 1;
    return { ...style, alignment: next };
};

/**
 * Translates every Hebrew text cell (and sheet name) of an xlsx-js-style workbook in place and
 * sets the sheet direction for `language`. No-op for the source language.
 */
export const translateWorkbook = async (wb, language, token) => {
    if (!wb || !language || language === SOURCE_REPORT_LANGUAGE) return wb;

    const textCells = [];
    Object.values(wb.Sheets || {}).forEach((ws) => {
        Object.keys(ws).forEach((address) => {
            if (address[0] === '!') return;
            const cell = ws[address];
            if (cell && typeof cell.v === 'string' && needsTranslation(cell.v)) textCells.push(cell);
        });
    });

    const translate = await createTranslator(
        [...textCells.map((c) => c.v), ...(wb.SheetNames || [])],
        language,
        token
    );

    textCells.forEach((cell) => {
        cell.v = translate(cell.v);
        delete cell.w;
        delete cell.h;
    });

    const ltr = !isRtlLanguage(language);
    if (ltr) {
        Object.values(wb.Sheets || {}).forEach((ws) => {
            Object.keys(ws).forEach((address) => {
                if (address[0] !== '!' && ws[address]?.s) ws[address].s = mirrorCellStyleToLtr(ws[address].s);
            });
        });
    }

    if (wb.SheetNames?.length) {
        const used = new Set();
        const renamed = wb.SheetNames.map((name) => {
            const base = translate(name).replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31) || name;
            let candidate = base;
            for (let n = 2; used.has(candidate); n += 1) candidate = `${base.slice(0, 28)} ${n}`;
            used.add(candidate);
            return candidate;
        });
        const sheets = {};
        wb.SheetNames.forEach((name, i) => {
            sheets[renamed[i]] = wb.Sheets[name];
        });
        wb.Sheets = sheets;
        wb.SheetNames = renamed;
    }

    return ltr ? applyWorkbookLtr(wb) : applyWorkbookRtl(wb);
};
